import { memo, PropsWithChildren, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { AppId } from 'clickNgo/configs/app'
import { WidgetStatus } from 'clickNgo/features/widgets'
import { selectWidgets } from 'clickNgo/features/widgets/selectors'
import { lazyRenderWidgetByLoader } from 'core-components/helpers/lazyLoad'
import { getUrlParam } from 'core-components/helpers/url'
import { Loader } from 'core-components/library/loader'
import { selectAppId } from 'core-components/sitebuilder/store/selectors/selectAppId'

import { IWidgetLazyRendererProps, IWidgetRendererProps } from './models'

export const WidgetRenderer = memo<PropsWithChildren<IWidgetRendererProps>>(
  ({
    children,
    dependencies,
    excludedPages,
    failedPlug,
    hasFailedPlug,
    hasLoaderPlug,
    loaderPlug,
  }) => {
    const page = getUrlParam('page')
    const appId = useSelector(selectAppId)
    const widgets = useSelector(selectWidgets)
    const isLutApp = appId === AppId.Lut
    const isPageExcluded = excludedPages?.includes(page)

    const isReadyToRender = useMemo(
      () => dependencies.every((widgetName) => widgets[widgetName].status === WidgetStatus.Ready),
      [dependencies, widgets]
    )

    const isLoading = useMemo(
      () => dependencies.some((widgetName) => widgets[widgetName].status === WidgetStatus.Loading),
      [dependencies, widgets]
    )

    const isFailed = useMemo(
      () => dependencies.some((widgetName) => widgets[widgetName].status === WidgetStatus.Failed),
      [dependencies, widgets]
    )

    if (isLutApp || isPageExcluded || isReadyToRender) {
      return <>{children}</>
    }

    if (hasLoaderPlug && isLoading) {
      return loaderPlug ? <>{loaderPlug}</> : <Loader />
    }

    if (hasFailedPlug && isFailed) {
      return failedPlug ? <>{failedPlug}</> : <Loader />
    }

    return null
  }
)

export const WidgetLazyRenderer = memo<IWidgetLazyRendererProps>(
  ({ id, props, loader, loadMeta, ...rest }) => {
    return (
      <WidgetRenderer {...rest}>
        {lazyRenderWidgetByLoader({ id, props, loader, loadMeta })}
      </WidgetRenderer>
    )
  }
)
